import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Target, Users, CheckCircle, ArrowLeft } from 'lucide-react';

const AIConsulting = () => {
  const approach = [
    {
      step: "01",
      title: "Consulting & Discovery",
      description: "Deep dive into your business objectives, current processes, and AI readiness assessment."
    },
    {
      step: "02",
      title: "Business Analysis",
      description: "Comprehensive analysis of opportunities, risks, and potential ROI for AI implementation."
    },
    {
      step: "03",
      title: "Proposal & Roadmap",
      description: "Detailed implementation roadmap with clear milestones, timelines, and success metrics."
    },
    {
      step: "04",
      title: "Implementation Support",
      description: "Ongoing guidance and support throughout the implementation process to ensure success."
    }
  ];

  const capabilities = [
    "AI strategy development and roadmap creation",
    "Business process analysis and optimization",
    "Technology assessment and vendor selection",
    "ROI modeling and business case development",
    "Risk assessment and mitigation planning",
    "Change management and stakeholder alignment"
  ];

  const benefits = [
    "Clear AI vision aligned with business objectives",
    "Systematic approach to AI adoption",
    "Risk mitigation through proven methodologies",
    "Measurable outcomes and KPIs",
    "Reduced implementation time and costs",
    "Sustainable competitive advantage"
  ];

  return (
    <div className="dark-theme pt-24">
      {/* Breadcrumb */}
      <section className="py-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 text-gray-400">
            <Link to="/solutions" className="hover:text-bluemp-400 transition-colors">Solutions</Link>
            <span>/</span>
            <span className="text-white">AI Consulting and Strategy</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/solutions" className="inline-flex items-center gap-2 text-bluemp-400 hover:text-bluemp-300 transition-colors mb-6">
                <ArrowLeft size={16} />
                Back to Solutions
              </Link>
              
              <h1 className="display-large text-white mb-6">
                From Insight to{' '}
                <span className="text-bluemp-400">Implementation</span>
              </h1>
              
              <p className="body-large text-gray-300 mb-8">
                Turning business challenges into executable AI roadmaps with clear KPIs and measurable outcomes. We help organizations move from AI exploration to strategic implementation with confidence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary">
                  Start Your AI Strategy
                  <ArrowRight size={20} />
                </Link>
                <Link to="/experience" className="btn-secondary">
                  View Case Studies
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <Brain size={64} className="text-bluemp-400 mb-6" />
                <h3 className="heading-2 text-white mb-4">Strategic AI Consulting</h3>
                <p className="body-medium text-gray-300 mb-6">
                  Transform your business with AI solutions that deliver measurable results while maintaining ethical standards and operational excellence.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Proven methodology</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Measurable outcomes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-bluemp-400" />
                    <span className="body-small text-gray-300">Ethical implementation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Our Proven Approach
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              A systematic methodology that ensures successful AI strategy development and implementation across organizations of all sizes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((phase, index) => (
              <div key={index} className="text-center group dark-hover">
                <div className="w-16 h-16 bg-bluemp-400/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-bluemp-400/30 transition-colors">
                  <span className="heading-2 text-bluemp-400">{phase.step}</span>
                </div>
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">{phase.title}</h3>
                <p className="body-small text-gray-300">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities & Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Core Capabilities */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Target size={32} className="text-bluemp-400" />
                <h2 className="display-medium text-white">Core Capabilities</h2>
              </div>
              
              <p className="body-large text-gray-300 mb-8">
                Comprehensive AI consulting services that cover every aspect of your transformation journey, from strategy to execution.
              </p>

              <ul className="space-y-4">
                {capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-medium text-gray-300">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Key Benefits */}
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Users size={32} className="text-bluemp-400" />
                <h2 className="display-medium text-white">Key Benefits</h2>
              </div>
              
              <p className="body-large text-gray-300 mb-8">
                Realize tangible business value through strategic AI implementation that delivers sustainable competitive advantages.
              </p>

              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-medium text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-6 bg-gray-900/50 backdrop-blur-sm rounded-lg border border-bluemp-400/20">
                <h3 className="heading-3 text-bluemp-400 mb-4">Success Guarantee</h3>
                <p className="body-medium text-gray-300">
                  We're committed to your success. Our consulting engagements include clear deliverables, measurable outcomes, and ongoing support to ensure your AI strategy delivers the expected business value.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Blue MP */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-6">
              Why Choose Blue MP for AI Consulting?
            </h2>
            <p className="body-large text-gray-300 max-w-2xl mx-auto">
              Our unique combination of business expertise, technical knowledge, and ethical approach sets us apart in the AI consulting landscape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Proven Experience",
                description: "Extensive experience across financial services, insurance, and communications sectors with measurable results.",
                metric: "10M+"
              },
              {
                title: "Ethical Approach",
                description: "Unwavering commitment to responsible AI implementation with transparency and human accountability.",
                metric: "100%"
              },
              {
                title: "Business Focus",
                description: "Deep understanding of business objectives ensures AI solutions deliver tangible value and ROI.",
                metric: "Multi-Million"
              }
            ].map((item, index) => (
              <div key={index} className="text-center group dark-hover p-6 rounded-lg bg-gray-900/30 backdrop-blur-sm border border-bluemp-400/20">
                <div className="display-large text-bluemp-400 mb-4">{item.metric}</div>
                <h3 className="heading-3 text-white mb-4 group-hover:text-bluemp-400 transition-colors">{item.title}</h3>
                <p className="body-small text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-6 lg:px-8">
          <h2 className="display-medium text-white mb-6">
            Ready to Develop Your AI Strategy?
          </h2>
          <p className="body-large text-gray-300 mb-8">
            Transform your business with a comprehensive AI strategy that delivers measurable results. Let's discuss your goals and create a roadmap for success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Book Strategy Consultation
              <ArrowRight size={20} />
            </Link>
            <Link to="/ai-learning" className="btn-secondary">
              Explore AI Learning
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIConsulting;